import {useTranslation} from '@/translations/i18n'
import {SpaceData} from './missing-coverage'
import {FC, useState} from 'react'
import {ModalBody, ModalCloseButton, ModalFooter, ModalHeader} from '@components/ui/modal-atoms'
import {Button} from '@components/ui/button'
import {ChevronRightIcon} from '@components/ui/icon'
import {formatLocaleDate} from '@/dates.ts'
import {PHOTOGRAPHER_RESOURCE_CENTER_URL} from '@features/insights/constants'
import {MissingCoverageFlaggedAssignment} from '@features/insights/types'
import {Separator} from '@components/ui/separator'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'

type MissingCoverageCardProps = SpaceData & {
    flaggedIn: MissingCoverageFlaggedAssignment[] | null
}

export const MissingCoverageCard: FC<MissingCoverageCardProps> = ({
    icon: Icon,
    label,
    value,
    descriptionCard,
    descriptionModal,
    flaggedIn
}) => {
    const {t} = useTranslation()
    const [isMissingCoverageModalOpen, setIsMissingCoverageModalOpen] = useState(false)
    const listingFallback = t('commons:listing')

    return (
        <>
            <div className="flex flex-col gap-2 rounded-none border-0 border-b border-neutral-300 bg-white px-0 py-4 md:row-span-6 md:grid md:grid-rows-subgrid md:gap-x-0 md:gap-y-2 md:rounded-2xl md:border md:border-neutral-300 md:p-4">
                <div className="flex w-full items-center gap-2 [&_p]:m-0 [&_p]:overflow-hidden [&_p]:text-ellipsis [&_p]:whitespace-nowrap [&_p]:text-md [&_p]:font-medium [&_p]:text-neutral-900 [&_svg]:h-6 [&_svg]:w-6 [&_svg]:shrink-0 [&_svg]:fill-neutral-900">
                    <Icon />
                    <p>{t(label, {value})}</p>
                </div>
                <p className="m-0 text-sm text-neutral-900">{t(descriptionCard)}</p>
                <Separator orientation="horizontal" />
                <p className="m-0 text-xs font-medium text-neutral-600">{t('insights:issues:flagged_in')}</p>
                <div className="flex w-full flex-1 flex-col gap-4 p-1">
                    {flaggedIn?.map(item => {
                        const title = item.entityTitle ?? listingFallback
                        const dateLabel = item.toShootAt ? formatLocaleDate(item.toShootAt, 'PP') : '-'

                        return (
                            <a
                                key={item.id}
                                href={item.listingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex cursor-pointer items-center justify-between gap-2 rounded-2xl bg-white p-4 text-inherit no-underline shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.18)]"
                            >
                                <div className="flex min-w-0 flex-col gap-2">
                                    <p
                                        className="m-0 text-md font-medium leading-5 text-neutral-900 [overflow-wrap:anywhere]"
                                        title={title}
                                    >
                                        {title}
                                    </p>
                                    <p className="m-0 text-sm leading-[18px] text-neutral-600">{dateLabel}</p>
                                </div>
                                <ChevronRightIcon className="h-3 w-3 shrink-0 fill-neutral-600" />
                            </a>
                        )
                    })}
                </div>

                <Button
                    variant="tertiary"
                    shape="text"
                    onClick={() => setIsMissingCoverageModalOpen(true)}
                    className="justify-self-end self-end text-sm font-medium text-neutral-900 underline"
                >
                    {t('commons:learn_more')}
                </Button>
            </div>

            {isMissingCoverageModalOpen && (
                <Dialog open={isMissingCoverageModalOpen} onOpenChange={setIsMissingCoverageModalOpen}>
                    <DialogContent className="w-[510px]" showCloseButton={false}>
                        <ModalHeader className="border-none">
                            <DialogClose data-slot="dialog-close" render={<ModalCloseButton />} />
                        </ModalHeader>

                        <ModalBody className="gap-4 h-auto">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center justify-center shrink-0 size-12 rounded-lg border border-neutral-300">
                                    <Icon className="size-7" />
                                </div>

                                <p className="text-xl font-medium text-neutral-900">{t(label, {value})}</p>
                            </div>
                            <span className="text-md font-normal">{t(descriptionModal)}</span>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="tertiary" onClick={() => setIsMissingCoverageModalOpen(false)}>
                                {t('commons:close')}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() =>
                                    window.open(PHOTOGRAPHER_RESOURCE_CENTER_URL, '_blank', 'noopener,noreferrer')
                                }
                            >
                                {t('insights:issues:open_full_guide')}
                            </Button>
                        </ModalFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    )
}
